// searchParamDecorators.ts
export type FieldSpec<T> = {
  key: string;
  default: T;
  parse: (raw: string | null) => T | undefined;
  serialize: (v: T) => string | undefined; // undefined => remove from URL
  setter?: string; // optional name of a setter method on the store
};

type FieldMap = Map<string | symbol, FieldSpec<any>>;
type Meta = { baseRoute?: string; fields: FieldMap };

const REGISTRY: WeakMap<Function, Meta> = new WeakMap();

const ensureMeta = (ctor: Function): Meta => {
  const existing = REGISTRY.get(ctor);
  if (existing) return existing;
  const meta: Meta = { fields: new Map() };
  REGISTRY.set(ctor, meta);
  return meta;
};

export const getSearchMeta = (ctor: Function) => ensureMeta(ctor);

type FieldOpts<T> = Partial<Pick<FieldSpec<T>, "key" | "default" | "parse" | "serialize" | "setter">>;

export const isSearchParam = <T>(opts: FieldOpts<T> = {}) => {
  return function (...args: any[]) {
    // Standard decorators path: (value, context)
    if (args.length === 2 && typeof args[1] === 'object' && args[1] && 'kind' in args[1]) {
      const _value = args[0];
      const context: ClassFieldDecoratorContext<any, T> = args[1];
      if (context.kind !== 'field') throw new Error('@isSearchParam can only decorate fields');
      const prop = context.name;

      const autoParse = (raw: string | null) => {
        if (raw == null) return undefined;
        // integer / number detection
        if (/^-?\d+$/.test(raw)) {
          const n = Number(raw);
            if (Number.isSafeInteger(n)) return n as any;
        }
        // boolean detection
        if (raw === '1' || raw === 'true') return true as any;
        if (raw === '0' || raw === 'false') return false as any;
        return raw as any;
      };
      const autoSerialize = (v: any) => {
        if (v == null) return undefined;
        if (typeof v === 'boolean') return v ? '1' : undefined; // elide false
        if (typeof v === 'number' && Number.isFinite(v)) return String(v);
        return String(v);
      };

      const makeSpec = (initialDefault: T): FieldSpec<T> => ({
        key: (opts.key ?? String(prop)) as string,
        default: (opts.default ?? initialDefault) as T,
        parse: opts.parse ?? autoParse,
        serialize: opts.serialize ?? autoSerialize,
        setter: opts.setter,
      });

      context.addInitializer(function () {
        const ctor = (this as any).constructor as Function;
        const meta = ensureMeta(ctor);
        const initialDefault = (this as any)[prop];
        meta.fields.set(prop, makeSpec(initialDefault));
      });
      return; // nothing to return for standard field decorator
    }

    // Legacy (Babel / TypeScript experimental) path: (targetPrototype, propertyKey)
    const target = args[0];
    const prop = args[1];
    if (!prop) throw new Error('@isSearchParam legacy mode: missing property key');

    const autoParse = (raw: string | null) => {
      if (raw == null) return undefined;
      if (/^-?\d+$/.test(raw)) {
        const n = Number(raw);
        if (Number.isSafeInteger(n)) return n as any;
      }
      if (raw === '1' || raw === 'true') return true as any;
      if (raw === '0' || raw === 'false') return false as any;
      return raw as any;
    };
    const autoSerialize = (v: any) => {
      if (v == null) return undefined;
      if (typeof v === 'boolean') return v ? '1' : undefined;
      if (typeof v === 'number' && Number.isFinite(v)) return String(v);
      return String(v);
    };

    const makeSpecLegacy = (initialDefault: T): FieldSpec<T> => ({
      key: (opts.key ?? String(prop)) as string,
      default: (opts.default ?? initialDefault) as T,
      parse: opts.parse ?? autoParse,
      serialize: opts.serialize ?? autoSerialize,
      setter: opts.setter,
    });

    const ctor: Function = target.constructor;
    const meta = ensureMeta(ctor);
    const initialDefault = (target as any)[prop]; // may be undefined if not initialized yet
    meta.fields.set(prop, makeSpecLegacy(initialDefault));
  } as any;
};

// Class decorator to declare a base route such as "/patient"
export const searchRoute = (route: string) => {
  return function (...args: any[]) {
    // Standard: (value, context)
    if (args.length === 2 && typeof args[1] === 'object' && args[1] && 'kind' in args[1]) {
      const value: Function = args[0];
      const meta = ensureMeta(value);
      meta.baseRoute = route;
      return;
    }
    // Legacy: (targetConstructor)
    const ctor: Function = args[0];
    const meta = ensureMeta(ctor);
    meta.baseRoute = route;
  } as any;
};
